/**
 * Renderer for the `choice_group` UI component (schema v1, see ./schema.ts).
 *
 * Shared and presentational: all text comes from the component or the caller's
 * props. Every option is a real <button> with `aria-pressed`, whatever the
 * `appearance` (a checkbox look is a drawing, not an <input>), because one click
 * submits. Labels are rendered as plain text.
 */
import type { ChoiceGroupComponent, ChoiceOption } from './schema';
import './ChoiceGroup.css';

interface ChoiceGroupProps {
  component: ChoiceGroupComponent;
  /** Accessible group name; also shown as the heading when the component has no title. */
  label: string;
  /** Option already chosen (live click or restored from history). */
  selectedId?: string;
  /** Read-only: shown, but no option can be chosen. */
  disabled?: boolean;
  /**
   * Caller's visual treatment. `follow-up`: the chat's next-step list (light
   * heading, stacked "↳" buttons, no card), whatever the component's appearance.
   */
  variant?: 'default' | 'follow-up';
  onSubmit: (option: ChoiceOption) => void;
}

export default function ChoiceGroup({ component, label, selectedId, disabled, variant = 'default', onSubmit }: ChoiceGroupProps) {
  const { appearance } = component.props;
  // A single-select group is spent once something is chosen: siblings go inactive.
  const inactive = disabled || selectedId !== undefined;
  const heading = component.title ?? label;
  return (
    <div
      className={[
        'ui-choice-group',
        variant === 'follow-up' ? 'ui-choice-group--follow-up' : `ui-choice-group--${appearance}`,
        inactive ? 'ui-choice-group--inactive' : '',
      ].filter(Boolean).join(' ')}
      role="group"
      aria-label={heading}
    >
      <div className="ui-choice-group__title">{heading}</div>
      <div className="ui-choice-group__options">
        {component.data.options.map(option => {
          const selected = option.id === selectedId;
          return (
            <button
              key={option.id}
              type="button"
              className={`ui-choice-group__option${selected ? ' ui-choice-group__option--selected' : ''}`}
              aria-pressed={selected}
              disabled={inactive}
              title={option.label}
              onClick={() => onSubmit(option)}
            >
              {variant === 'follow-up' && (
                <span className="ui-choice-group__arrow" aria-hidden="true">↳</span>
              )}
              {variant === 'default' && (appearance === 'checkbox' || appearance === 'radio') && (
                <span className="ui-choice-group__mark" aria-hidden="true">
                  {selected ? '✓' : ''}
                </span>
              )}
              <span className="ui-choice-group__label">{option.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
